import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { collection, getDocs } from "firebase/firestore";
import PaymentForm from "./paymentForm";
import { db } from "../firebase";
import { fetchQuote } from "../api";
import { ChevronLeft, ChevronRight, CalendarSmall } from "./icons";

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const WEEKDAYS = ["LUN", "MAR", "MER", "JEU", "VEN", "SAM", "DIM"];

/**
 * La clé publiable Stripe (pk_...) est publique par nature : elle vit dans
 * .env.local, pas dans Firestore. La clé secrète, elle, ne quitte jamais
 * Secret Manager côté Cloud Functions.
 */
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const toKey = (year, month, day) =>
  `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

/** Grille du mois : cases vides en tête pour aligner sur lundi. */
const buildMonth = (year, month) => {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7; // 0 = lundi
  const total = new Date(year, month + 1, 0).getDate();
  return [
    ...Array.from({ length: offset }, () => null),
    ...Array.from({ length: total }, (_, i) => i + 1),
  ];
};

const Calendar = ({ selected, onSelect, unavailable }) => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const cells = useMemo(() => buildMonth(year, month), [year, month]);

  const move = (delta) => setCursor(new Date(year, month + delta, 1));

  return (
    <div className="calendar">
      <div className="calendar-head">
        <button type="button" onClick={() => move(-1)} aria-label="Mois précédent">
          <ChevronLeft size={18} />
        </button>
        <span>
          {MONTHS[month]} {year}
        </span>
        <button type="button" onClick={() => move(1)} aria-label="Mois suivant">
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="calendar-grid calendar-weekdays">
        {WEEKDAYS.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>

      <div className="calendar-grid">
        {cells.map((day, i) => {
          if (!day) return <span key={`empty-${i}`} className="calendar-cell is-empty" />;

          const key = toKey(year, month, day);
          const isPast = new Date(year, month, day) < today;
          const isBusy = isPast || unavailable.has(key);

          return (
            <button
              type="button"
              key={key}
              disabled={isBusy}
              onClick={() => onSelect(key)}
              className={`calendar-cell ${isBusy ? "is-busy" : ""} ${
                selected === key ? "is-selected" : ""
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>

      <div className="calendar-legend">
        <span><i className="dot dot-free" /> Disponible</span>
        <span><i className="dot dot-busy" /> Indisponible</span>
        <span><i className="dot dot-sel" /> Sélectionné</span>
      </div>
    </div>
  );
};

const initialTrip = {
  date: "",
  time: "10:00",
  departure: "",
  arrival: "",
  passengers: "1",
  tripType: "aller-simple",
  childSeat: false,
  wheelchair: false,
  email: "",
};

export const Booking = () => {
  const [trip, setTrip] = useState(initialTrip);
  const [unavailable, setUnavailable] = useState(() => new Set());
  const [quote, setQuote] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });
  const [payment, setPayment] = useState(false);

  // Dates déjà réservées. La collection `availability` ne contient que des
  // identifiants de date : aucune donnée personnelle n'est exposée.
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "availability"));
        setUnavailable(new Set(snap.docs.map((d) => d.id)));
      } catch (e) {
        console.warn("Calendrier des disponibilités indisponible :", e.message);
      }
    })();
  }, []);

  const reset = () => {
    setQuote(null);
    setPayment(false);
    setStatus({ loading: false, error: null });
  };

  const update = (e) => {
    const { name, value, type, checked } = e.target;
    setTrip((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    reset();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!trip.date) {
      setStatus({ loading: false, error: "Choisissez une date dans le calendrier." });
      return;
    }

    setStatus({ loading: true, error: null });
    try {
      const result = await fetchQuote({
        departure: trip.departure,
        arrival: trip.arrival,
        tripType: trip.tripType,
        date: trip.date,
        time: trip.time,
      });
      setQuote(result);
      setStatus({ loading: false, error: null });
    } catch (err) {
      setQuote(null);
      setStatus({ loading: false, error: err.message });
    }
  };

  return (
    <section id="reservation" className="booking-card">
      <div className="booking-head">
        <h2>Réservez votre trajet en ligne</h2>
        <p>Choisissez votre date, heure et réservez en quelques clics.</p>
      </div>

      <div className="booking-body">
        <Calendar
          selected={trip.date}
          unavailable={unavailable}
          onSelect={(date) => {
            setTrip((p) => ({ ...p, date }));
            reset();
          }}
        />

        <form className="booking-form" onSubmit={submit}>
          <div className="field-row">
            <label className="field">
              <span>Date</span>
              <span className="input-with-icon">
                <input type="date" name="date" value={trip.date} onChange={update} required />
                <CalendarSmall size={16} />
              </span>
            </label>

            <label className="field">
              <span>Heure</span>
              <select name="time" value={trip.time} onChange={update}>
                {Array.from({ length: 48 }, (_, i) => {
                  const h = String(Math.floor(i / 2)).padStart(2, "0");
                  const m = i % 2 ? "30" : "00";
                  return (
                    <option key={`${h}:${m}`} value={`${h}:${m}`}>
                      {h}:{m}
                    </option>
                  );
                })}
              </select>
            </label>
          </div>

          <label className="field">
            <span>Départ</span>
            <input
              type="text"
              name="departure"
              placeholder="Adresse de départ"
              value={trip.departure}
              onChange={update}
              required
            />
          </label>

          <label className="field">
            <span>Destination</span>
            <input
              type="text"
              name="arrival"
              placeholder="Adresse de destination"
              value={trip.arrival}
              onChange={update}
              required
            />
          </label>

          <div className="field-row">
            <label className="field">
              <span>Passagers</span>
              <select name="passengers" value={trip.passengers} onChange={update}>
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <option key={n} value={n}>
                    {n} passager{n > 1 ? "s" : ""}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>Type de trajet</span>
              <select name="tripType" value={trip.tripType} onChange={update}>
                <option value="aller-simple">Aller simple</option>
                <option value="aller-retour">Aller-retour</option>
                <option value="mise-a-disposition">Mise à disposition</option>
              </select>
            </label>
          </div>

          <div className="field">
            <span>Options</span>
            <div className="options-row">
              <label className="checkbox">
                <input
                  type="checkbox"
                  name="childSeat"
                  checked={trip.childSeat}
                  onChange={update}
                />
                <span>Siège enfant</span>
              </label>
              <label className="checkbox">
                <input
                  type="checkbox"
                  name="wheelchair"
                  checked={trip.wheelchair}
                  onChange={update}
                />
                <span>Transport PMR</span>
              </label>
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={status.loading}>
            {status.loading ? "Calcul en cours…" : "Vérifier la disponibilité"}
          </button>

          {status.error && <p className="form-error">{status.error}</p>}

          {quote && (
            <div className="quote">
              <p>
                {quote.from} → {quote.to}
              </p>
              <p>
                Distance : <strong>{quote.distanceKm} km</strong>
                {quote.durationMin ? ` · environ ${quote.durationMin} min` : ""}
              </p>
              <p>
                Tarif de base : <strong>{quote.basePrice} €</strong>{" "}
                <span className="quote-note">({quote.pricePerKm} €/km)</span>
              </p>

              {quote.surcharge.percent > 0 && (
                <p className="quote-surcharge">
                  {quote.surcharge.label} : +{quote.surcharge.percent}% (+
                  {quote.surcharge.amount} €)
                </p>
              )}

              <p className="quote-price">
                Estimation totale : <strong>{quote.price} €</strong>
              </p>
              <p className="quote-note">Tarif indicatif, recalculé et confirmé au paiement.</p>

              {!payment && (
                <>
                  <label className="field">
                    <span>Email pour la confirmation et le reçu</span>
                    <input
                      type="email"
                      name="email"
                      placeholder="vous@exemple.fr"
                      value={trip.email}
                      onChange={(e) => setTrip((p) => ({ ...p, email: e.target.value }))}
                      required
                    />
                  </label>
                  <button
                    type="button"
                    className="btn btn-dark btn-block"
                    disabled={!trip.email || !stripePromise}
                    onClick={() => setPayment(true)}
                  >
                    {stripePromise ? "Procéder au paiement" : "Paiement indisponible"}
                  </button>
                  {!stripePromise && (
                    <p className="quote-note">
                      Clé Stripe non configurée — contactez-nous pour finaliser la réservation.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {payment && stripePromise && (
            <Elements stripe={stripePromise}>
              <PaymentForm trip={trip} estimate={quote.price} />
            </Elements>
          )}
        </form>
      </div>
    </section>
  );
};

export default Booking;
